import classnames from 'classnames';
import {
  Children,
  cloneElement,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type FunctionComponent,
  type ReactElement,
  type ReactNode,
} from 'react';

import { getTabItemValue } from './utils/getTabItemValue';

import styles from './Tab.module.scss';

interface ItemProps {
  isWordWrap?: boolean;
  testId?: string;
}

interface TabProps {
  /* Every tabItem has "100% / tabs.length" width */
  areAllTabsSameWidth?: boolean;
  /** Any node or string to be wrapped with */
  children?: ReactNode;
  /** To override the style */
  className?: string;
  /** To word wrap the content */
  isWordWrap?: boolean;
  /** A callback on selecting a TabItem, with arguments event and value */
  onChange?: (event: ChangeEvent, value: any) => unknown;
  /** The initial value to be selected */
  testId?: string;
  value?: string;
}

export const Tab: FunctionComponent<TabProps> = ({
  areAllTabsSameWidth = true,
  children,
  className,
  isWordWrap = false,
  onChange,
  testId,
  value,
  ...restProps
}) => {
  const ref = useRef<HTMLUListElement>(null);
  const [cursorWidth, setCursorWidth] = useState<string | number | undefined>();
  const [cursorLeft, setCursorLeft] = useState<string | number | undefined>(0);

  const childs = Children.toArray(children).filter(
    (child) =>
      (child as ReactElement<unknown, FunctionComponent>)?.type?.displayName ===
      'TabItem',
  );

  const selectedIndex = childs.findIndex(
    (child) => getTabItemValue((child as ReactElement).props) === value,
  );
  const isSelected = selectedIndex !== -1;
  const itemWidth = 100 / childs.length;

  useEffect(() => {
    if (areAllTabsSameWidth) {
      setCursorWidth(`${itemWidth}%`);
      setCursorLeft(selectedIndex ? `${itemWidth * selectedIndex}%` : 0);
    } else {
      const updateCursor = (element: Element | null) => {
        const selectedItem = element?.children?.[selectedIndex];
        if (!selectedItem) return;

        const { offsetWidth: width, offsetLeft: left } =
          selectedItem as HTMLElement;
        setCursorWidth(width && `${width}px`);
        setCursorLeft(left && `${left}px`);
      };

      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          updateCursor(entry.target);
        }
      });

      updateCursor(ref.current);
      resizeObserver.observe(ref.current as HTMLElement);
      return () => resizeObserver.disconnect();
    }
  }, [selectedIndex, areAllTabsSameWidth, itemWidth]);

  if (!childs.length) {
    return null;
  }

  function onMenuSelect(event: ChangeEvent<HTMLElement>) {
    event.persist();
    if (onChange) {
      const { currentTarget } = event;
      onChange(event, getTabItemValue(currentTarget));
    }
  }

  const itemProps: ItemProps = {
    isWordWrap,
  };
  if (testId) {
    // To have a common behavior with ButtonGroup & CheckerInputGroup
    itemProps.testId = `${testId}-item`;
  }

  return (
    <div
      className={classnames(styles.root, className)}
      data-testid={testId}
      {...restProps}
    >
      <ul
        className={classnames(styles.tablist, {
          [styles.fitContentTablist]: !areAllTabsSameWidth,
        })}
        role="tablist"
        ref={ref}
      >
        {childs.map((child, index) =>
          cloneElement(child as ReactElement, {
            ...itemProps,
            isSelected: selectedIndex === index,
            onClick: (event: ChangeEvent<HTMLLIElement>) => {
              if ((child as ReactElement).props.onClick) {
                (child as ReactElement).props.onClick(event);
              }
              onMenuSelect(event);
            },
            width: areAllTabsSameWidth ? itemWidth : undefined,
          }),
        )}
      </ul>
      {isSelected && (
        <hr
          aria-hidden="true"
          className={styles.cursor}
          style={{
            width: cursorWidth,
            left: cursorLeft,
          }}
        />
      )}
    </div>
  );
};

Tab.displayName = 'Tab';
